import { storedRoutePrefix } from './route';

/**
 * The backend's bounds. A value past them would be answered with a 400, which the page
 * can only show as a generic load error, so it is treated as no filter instead.
 */
export const AUDIT_FILTER_MAX_LENGTH = {
    entryId: 19,
    actorEmail: 255,
    impersonatorEmail: 255,
    action: 100,
    entityType: 50,
    entityId: 255,
    entityLabel: 255,
    requestId: 255,
    ip: 45,
    route: 255,
    userAgent: 500,
    changedField: 64,
    metadataPath: 324,
    /** changedFrom, changedTo and metadataValue: the sanitizer's own string cap. */
    value: 1000,
} as const;

/** actions, excludeActions and entityType take at most this many values. */
export const AUDIT_FILTER_MAX_ITEMS = 100;

const MAX_INT4 = 2147483647;
const MAX_BIGINT = '9223372036854775807';
const DIGITS = /^\d+$/;
const CHANGED_FIELD = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;
const METADATA_PATH = /^[A-Za-z0-9_-]{1,64}(\.[A-Za-z0-9_-]{1,64}){0,4}$/;
const IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
const HEX_GROUP = /^[0-9A-Fa-f]{1,4}$/;

/** The id columns are int4. */
export function isAuditId(value: number): boolean {
    return Number.isSafeInteger(value) && value > 0 && value <= MAX_INT4;
}

export function parseAuditId(text: string): number | undefined {
    const trimmed = text.trim();
    if (!DIGITS.test(trimmed)) return undefined;
    const id = Number(trimmed);
    return isAuditId(id) ? id : undefined;
}

/** Only denied and failed entries carry a code, and it is always an HTTP status. */
export function isStatusCode(value: number): boolean {
    return Number.isInteger(value) && value >= 100 && value <= 599;
}

export function parseStatusCode(text: string): number | undefined {
    const trimmed = text.trim();
    if (!DIGITS.test(trimmed)) return undefined;
    const code = Number(trimmed);
    return isStatusCode(code) ? code : undefined;
}

/** Entry ids are bigint, so they are compared as digit strings rather than numbers. */
export function isEntryId(text: string): boolean {
    if (!DIGITS.test(text) || text.length > MAX_BIGINT.length) return false;
    return text.length < MAX_BIGINT.length || text <= MAX_BIGINT;
}

export function isChangedField(text: string): boolean {
    return CHANGED_FIELD.test(text);
}

export function isMetadataPath(text: string): boolean {
    return METADATA_PATH.test(text);
}

function isIpv6(text: string): boolean {
    const halves = text.split('::');
    if (halves.length > 2) return false;
    const groups = halves.map((half) => (half === '' ? [] : half.split(':')));
    const last = groups[groups.length - 1];
    // An IPv4 tail, as in ::ffff:192.0.2.1, stands for the last two groups.
    const hasIpv4Tail = last.length > 0 && IPV4.test(last[last.length - 1]);
    if (hasIpv4Tail) last.pop();
    const count = groups.reduce((sum, part) => sum + part.length, 0) + (hasIpv4Tail ? 2 : 0);
    const valid = groups.every((part) => part.every((group) => HEX_GROUP.test(group)));
    return valid && (halves.length === 2 ? count < 8 : count === 8);
}

/**
 * The backend answers anything but a plain address with a 400. Zone ids and prefixes
 * are refused here too, since an exact match on an inet column can find neither.
 */
export function isIpAddress(text: string): boolean {
    return IPV4.test(text) || (text.includes(':') && isIpv6(text));
}

/** The backend refuses NUL and lone surrogates, which Postgres text cannot store. */
export function isStorableText(text: string): boolean {
    for (let index = 0; index < text.length; index += 1) {
        const code = text.charCodeAt(index);
        if (code === 0) return false;
        if (code >= 0xd800 && code <= 0xdbff) {
            const next = text.charCodeAt(index + 1);
            if (!(next >= 0xdc00 && next <= 0xdfff)) return false;
            index += 1;
        } else if (code >= 0xdc00 && code <= 0xdfff) {
            return false;
        }
    }
    return true;
}

/**
 * A route typed as the table shows it is sent with its API prefix, so the backend's length
 * bound applies to the prefixed text, not to what was typed.
 */
export function isRouteFilter(text: string): boolean {
    return storedRoutePrefix(text).length <= AUDIT_FILTER_MAX_LENGTH.route;
}
